import React, { useEffect } from 'react';
import { Bell, Loader2, Mail, Save, Smartphone } from 'lucide-react';
import {
  useGetPreferencesQuery,
  useUpdatePreferencesMutation,
  UserPreferences,
} from '../../services/profileApi';
import { useToast } from '../../contexts/ToastContext';

interface NotificationToggle {
  key: string;
  label: string;
  description: string;
}

const EMAIL_NOTIFICATIONS: NotificationToggle[] = [
  {
    key: 'projectUpdates',
    label: 'Project Updates',
    description: 'Receive emails when projects are created, updated, or completed',
  },
  {
    key: 'taskAssignments',
    label: 'Task Assignments',
    description: 'Get notified when tasks are assigned to you',
  },
  {
    key: 'meetingReminders',
    label: 'Meeting Reminders',
    description: 'Receive email reminders before scheduled meetings',
  },
  {
    key: 'dailyDigest',
    label: 'Daily Digest',
    description: 'Get a daily summary of activity across your projects',
  },
  {
    key: 'weeklyReport',
    label: 'Weekly Report',
    description: 'Receive a weekly progress report for all your projects',
  },
];

const PUSH_NOTIFICATIONS: NotificationToggle[] = [
  {
    key: 'enabled',
    label: 'Enable Push Notifications',
    description: 'Receive push notifications on this device',
  },
  {
    key: 'urgent',
    label: 'Urgent Notifications',
    description: 'Get immediate alerts for urgent items and deadlines',
  },
  {
    key: 'mentions',
    label: 'Mentions',
    description: 'Get notified when someone mentions you in comments',
  },
];

const ProfileNotifications: React.FC = () => {
  const { success, error: showError } = useToast();
  const { data: preferences, isLoading, error } = useGetPreferencesQuery();
  const [updatePreferences, { isLoading: isSaving }] = useUpdatePreferencesMutation();

  const [localPrefs, setLocalPrefs] = React.useState<UserPreferences['notifications'] | null>(null);
  const [hasChanges, setHasChanges] = React.useState(false);

  // Initialize local state when preferences load
  useEffect(() => {
    if (preferences?.notifications) {
      setLocalPrefs(preferences.notifications);
    }
  }, [preferences]);

  const handleEmailToggle = (key: string) => {
    if (!localPrefs) return;

    setLocalPrefs(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        email: {
          ...prev.email,
          [key]: !prev.email[key as keyof typeof prev.email],
        },
      };
    });
    setHasChanges(true);
  };

  const handlePushToggle = (key: string) => {
    if (!localPrefs) return;

    setLocalPrefs(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        push: {
          ...prev.push,
          [key]: !prev.push[key as keyof typeof prev.push],
        },
      };
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!localPrefs) return;

    try {
      await updatePreferences({
        notifications: localPrefs,
      }).unwrap();
      success('Notification preferences saved');
      setHasChanges(false);
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to save preferences');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          Failed to load notification preferences. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 max-w-4xl">
      {/* Page Header */}
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-gray-900">Notifications</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Manage how you receive notifications and updates
        </p>
      </div>

      {/* Email Notifications */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center">
            <Mail className="w-4 h-4 text-brand-primary" />
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-900">Email Notifications</h2>
            <p className="text-xs text-gray-500">Choose which emails you want to receive</p>
          </div>
        </div>

        <div className="space-y-4">
          {EMAIL_NOTIFICATIONS.map(notification => (
            <div
              key={notification.key}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div className="flex-1 pr-4">
                <h3 className="text-sm font-medium text-gray-900">{notification.label}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{notification.description}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={
                  localPrefs?.email?.[notification.key as keyof typeof localPrefs.email] ?? false
                }
                onClick={() => handleEmailToggle(notification.key)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 ${
                  localPrefs?.email?.[notification.key as keyof typeof localPrefs.email]
                    ? 'bg-brand-primary'
                    : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    localPrefs?.email?.[notification.key as keyof typeof localPrefs.email]
                      ? 'translate-x-6'
                      : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Push Notifications */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center">
            <Smartphone className="w-4 h-4 text-brand-primary" />
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-900">Push Notifications</h2>
            <p className="text-xs text-gray-500">Manage browser and mobile push notifications</p>
          </div>
        </div>

        <div className="space-y-4">
          {PUSH_NOTIFICATIONS.map(notification => (
            <div
              key={notification.key}
              className={`flex items-center justify-between p-4 bg-gray-50 rounded-lg ${
                notification.key !== 'enabled' && !localPrefs?.push?.enabled ? 'opacity-50' : ''
              }`}
            >
              <div className="flex-1 pr-4">
                <h3 className="text-sm font-medium text-gray-900">{notification.label}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{notification.description}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={
                  localPrefs?.push?.[notification.key as keyof typeof localPrefs.push] ?? false
                }
                onClick={() => handlePushToggle(notification.key)}
                disabled={notification.key !== 'enabled' && !localPrefs?.push?.enabled}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 disabled:cursor-not-allowed ${
                  localPrefs?.push?.[notification.key as keyof typeof localPrefs.push]
                    ? 'bg-brand-primary'
                    : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    localPrefs?.push?.[notification.key as keyof typeof localPrefs.push]
                      ? 'translate-x-6'
                      : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      {hasChanges && (
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-3 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors shadow-lg disabled:opacity-50 flex items-center space-x-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Notification Tips */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
        <div className="flex items-center space-x-2 mb-3">
          <Bell className="w-4 h-4 text-gray-600" />
          <h3 className="text-sm font-medium text-gray-900">Notification Tips</h3>
        </div>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start space-x-2">
            <span className="text-brand-primary mt-1">•</span>
            <span>Enable push notifications for real-time alerts on urgent matters.</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-brand-primary mt-1">•</span>
            <span>Daily digests are great for staying updated without email overload.</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-brand-primary mt-1">•</span>
            <span>You can always mute specific projects in their settings.</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ProfileNotifications;
